/**
 * BranchDeleteModal — sube KALICI (hard) silme onay modali.
 *
 * Akis: modal acilir -> `GET /api/Branch/usage` ile sube tum sistemde taranir
 * (satis ekranlari, kasa/banka, personel, stok, gecmis hareketler...) -> subeye
 * bagli kayit varsa silme butonu KAPALI kalir ve kullaniciya hangi alanlardan
 * kaldirmasi gerektigi listelenir. Liste bossa kalici silme yapilabilir.
 *
 * Kontrol sunucuda da tekrarlanir (POST /api/Branch/delete 409 dondurur), bu
 * yuzden istemci denetimi atlansa dahi kullanimda olan sube silinemez.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import BlockIcon from '@mui/icons-material/Block';
import { useTranslation } from 'react-i18next';
import {
  BranchInUseError,
  deleteBranch,
  getBranchUsage,
  type BranchUsageItem
} from '../../services/migration/branchDataService';

export interface BranchDeleteTarget {
  id: string;
  name: string;
}

interface BranchDeleteModalProps {
  open: boolean;
  branch: BranchDeleteTarget | null;
  onClose: () => void;
  onDeleted: (branch: BranchDeleteTarget) => void;
}

const BranchDeleteModal: React.FC<BranchDeleteModalProps> = ({ open, branch, onClose, onDeleted }) => {
  const { t } = useTranslation('branches');

  const [checking, setChecking] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);
  const [usages, setUsages] = useState<BranchUsageItem[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const runUsageCheck = useCallback(async (branchId: string) => {
    setChecking(true);
    setCheckFailed(false);
    setDeleteError(null);
    try {
      const result = await getBranchUsage(branchId);
      setUsages(result.usages);
    } catch (error) {
      console.error('[BranchDeleteModal] Şube kullanım kontrolü başarısız:', error);
      setUsages([]);
      setCheckFailed(true);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !branch?.id) return;
    setUsages([]);
    setDeleteError(null);
    runUsageCheck(branch.id);
  }, [open, branch?.id, runUsageCheck]);

  const inUse = usages.length > 0;
  const canDelete = !checking && !checkFailed && !inUse && !deleting;

  const handleDelete = async () => {
    if (!branch?.id) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteBranch(branch.id, true);
      onDeleted(branch);
    } catch (error) {
      if (error instanceof BranchInUseError) {
        // Denetim ile silme arasinda yeni kayit olusmus: listeyi guncelle.
        setUsages(error.usages);
        setDeleteError(t('deleteBranch.inUseRaceMessage'));
      } else {
        console.error('[BranchDeleteModal] Şube silinemedi:', error);
        setDeleteError(t('deleteBranch.deleteErrorMessage'));
      }
    } finally {
      setDeleting(false);
    }
  };

  const areaLabel = (usage: BranchUsageItem) =>
    t(`deleteBranch.areas.${usage.key}`, { defaultValue: usage.label || usage.key });

  return (
    <Dialog open={open} onClose={deleting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: 'error.main',
          color: 'error.contrastText',
          py: 1.75
        }}
      >
        <WarningAmberIcon />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
            {t('deleteBranch.title')}
          </Typography>
          {branch?.name && (
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {branch.name}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          disabled={deleting}
          aria-label={t('common.close')}
          sx={{ color: 'inherit' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {checking && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              {t('deleteBranch.checking')}
            </Typography>
          </Box>
        )}

        {!checking && checkFailed && (
          <Alert severity="error">
            <AlertTitle>{t('deleteBranch.checkErrorTitle')}</AlertTitle>
            {t('deleteBranch.checkErrorDescription')}
          </Alert>
        )}

        {!checking && !checkFailed && inUse && (
          <>
            <Alert severity="warning" icon={<BlockIcon />}>
              <AlertTitle>{t('deleteBranch.inUseTitle')}</AlertTitle>
              {t('deleteBranch.inUseDescription')}
            </Alert>
            <List dense sx={{ mt: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              {usages.map((usage) => (
                <ListItem key={usage.key} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <BlockIcon fontSize="small" color="warning" />
                  </ListItemIcon>
                  <ListItemText primary={areaLabel(usage)} />
                  <Chip
                    size="small"
                    variant="outlined"
                    color="warning"
                    label={
                      typeof usage.count === 'number'
                        ? t('deleteBranch.recordCount', { count: usage.count })
                        : t('deleteBranch.hasRecords')
                    }
                  />
                </ListItem>
              ))}
            </List>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t('deleteBranch.cleanupHint')}
            </Typography>
          </>
        )}

        {!checking && !checkFailed && !inUse && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('deleteBranch.notInUse')}
            </Alert>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {t('deleteBranch.confirmQuestion', { branchName: branch?.name || '' })}
            </Typography>
            <Alert severity="error" sx={{ mt: 2 }}>
              <AlertTitle>{t('deleteBranch.irreversibleTitle')}</AlertTitle>
              {t('deleteBranch.irreversibleDescription')}
            </Alert>
          </>
        )}

        {deleteError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {deleteError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={deleting}>
          {t('common.cancel')}
        </Button>
        {(inUse || checkFailed) && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={<RefreshIcon />}
            onClick={() => branch?.id && runUsageCheck(branch.id)}
            disabled={checking || deleting}
          >
            {t('deleteBranch.recheck')}
          </Button>
        )}
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={!canDelete}
          startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}
        >
          {deleting ? t('deleteBranch.deleting') : t('deleteBranch.confirmButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BranchDeleteModal;
